import React, { useEffect, useState } from 'react';
import { FieldError } from '@spike/model';
import {
    AddOnServiceOrderItem,
    AppointmentOrderItem,
    CustomOrderItem,
    Discount,
    Order,
    OrderizableTypes,
    ProductOrderItem,
    SaveOrder
} from '@spike/order-model';
import { Client } from '@spike/client-model';
import { fetchProductsThunk } from '@spike/products-action';
import { fetchThunk as fetchAddOnServicesThunk } from '@spike/addon-services-action';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import { useSingleSaleStyles } from 'components/Payments/SingleSale/SingleSale.styles';
import { SingleSaleHeader } from 'components/Payments/SingleSale/Header/SingleSaleHeader';
import { SingleSaleTable } from 'components/Payments/SingleSale/Table/SingleSaleTable';
import { ClientsState, ClientsStatus, getClientProfileOverviewThunk, getClientThunk } from '@spike/clients-action';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import moment from 'moment';
import { OverFullWindow, Spinner } from 'components/UI';
import { Client as ClientPage } from 'pages/Client';
import { showErrorThunk } from '@spike/notifications-action';
import { isEmpty, uniq } from 'lodash';
import { convertToNumber } from '@spike/price-utils';
import { deleteOrderItemThunk, getOrderThunk, OrdersState, OrdersStatus, saveOrderThunk } from '@spike/orders-action';
import { v4 as uuid } from 'uuid';
import { SingleSaleSuccess } from 'components/Payments/SingleSale/Success/SingleSaleSuccess';
import Checkout from 'components/Checkout2';
import { hashForUrl } from 'utils/HashUtils';

export type GenericOrderItem = AppointmentOrderItem | ProductOrderItem | AddOnServiceOrderItem | CustomOrderItem;

export const isAppointmentOrderItem = (orderItem: GenericOrderItem): orderItem is AppointmentOrderItem => {
    return orderItem.orderItem.orderizable.type === OrderizableTypes.APPOINTMENT;
};

export const isProductOrderItem = (orderItem: GenericOrderItem): orderItem is ProductOrderItem => {
    return orderItem.orderItem.orderizable.type === OrderizableTypes.PRODUCT;
};

export const isAddOnServiceOrderItem = (orderItem: GenericOrderItem): orderItem is AddOnServiceOrderItem => {
    return orderItem.orderItem.orderizable.type === OrderizableTypes.ADDON_SERVICE;
};

export const isCustomOrderItem = (orderItem: GenericOrderItem): orderItem is CustomOrderItem => {
    return orderItem.orderItem.orderizable.type === OrderizableTypes.CUSTOM;
};

export const updateOrderItemDiscount = (orderItem: GenericOrderItem, discount: Discount) => {
    const orderItemData = {
        ...orderItem,
        orderItem: {
            ...orderItem.orderItem,
            discount
        }
    };

    return {
        ...orderItemData,
        orderItem: {
            ...orderItemData.orderItem,
            amount: getOrderItemAmount(orderItemData, true),
            total: getOrderItemSubtotal(orderItemData, true)
        }
    };
};

export const updateOrderItemQuantity = (orderItem: GenericOrderItem, quantity: number) => {
    const orderItemData = {
        ...orderItem,
        orderItem: {
            ...orderItem.orderItem,
            quantity
        }
    };

    return {
        ...orderItemData,
        orderItem: {
            ...orderItemData.orderItem,
            amount: getOrderItemAmount(orderItemData, true),
            total: getOrderItemSubtotal(orderItemData, true)
        }
    };
};

export const getOrderItemAmount = <T extends boolean = false>(
    orderItem: GenericOrderItem,
    format?: T
): T extends true ? string : number => {
    let amount = 0;

    if (isProductOrderItem(orderItem) && orderItem.product?.id) {
        amount = Number(orderItem.product.businessProduct?.pricing.price);
    }

    if (isAddOnServiceOrderItem(orderItem) && orderItem.addOnService?.id) {
        amount = Number(orderItem.addOnService.price);
    }

    if (isCustomOrderItem(orderItem) && orderItem.orderItem.amount) {
        amount = Number(orderItem.orderItem.amount);
    }

    if (format) {
        return amount.toFixed(2) as T extends true ? string : number;
    }

    return amount as T extends true ? string : number;
};

export const getOrderItemSubtotal = <T extends boolean = false>(
    item: GenericOrderItem,
    format?: T
): T extends true ? string : number => {
    let total = 0;

    total = getOrderItemAmount(item) * item.orderItem.quantity;

    if (item.orderItem.discount?.percentage) {
        total = total * (1 - item.orderItem.discount.percentage / 100);
    }

    if (item.orderItem.discount?.amount) {
        total = total - Number(item.orderItem.discount.amount);
    }

    if (format) {
        return total.toFixed(2) as T extends true ? string : number;
    }

    return total as T extends true ? string : number;
};

export const validateOrder = (order: Order): Array<FieldError> => {
    const errors: Array<FieldError> = [];

    const orderItems: Array<GenericOrderItem> = [
        ...order.items.appointments,
        ...order.items.products,
        ...order.items.addOnServices,
        ...order.items.customs
    ];

    if (order.client?.id === undefined) {
        errors.push({
            fieldName: `client`,
            errorMessage: `Select a client.`
        });
    }

    orderItems
        .filter(
            item =>
                (item.orderItem.discount?.amount &&
                    item.orderItem.total &&
                    convertToNumber(item.orderItem.discount.amount) >
                        convertToNumber(item.orderItem.amount) * item.orderItem.quantity) ||
                (item.orderItem.discount?.percentage && item.orderItem.discount.percentage > 100)
        )
        .forEach(item => {
            const totalNoDiscounts = Number(item.orderItem.amount) * Number(item.orderItem.quantity);

            errors.push({
                fieldName: `${item.orderItem.uuid}_discount`,
                errorMessage: `Discount cannot be greater than $${totalNoDiscounts.toFixed(2)}`
            });
        });

    orderItems
        .filter(item => isProductOrderItem(item) && item.product?.id === undefined)
        .forEach(item => {
            errors.push({ fieldName: `${item.orderItem.uuid}_selection`, errorMessage: `Select a product.` });
        });

    orderItems
        .filter(item => isAddOnServiceOrderItem(item) && item.addOnService?.id === undefined)
        .forEach(item => {
            errors.push({ fieldName: `${item.orderItem.uuid}_selection`, errorMessage: `Select an add-on service.` });
        });

    orderItems
        .filter(item => isProductOrderItem(item) && item.product?.id)
        .forEach(item => {
            if (
                orderItems.some(
                    otherItem =>
                        isProductOrderItem(item) &&
                        isProductOrderItem(otherItem) &&
                        otherItem.orderItem.uuid !== item.orderItem.uuid &&
                        otherItem.product?.id === item.product?.id
                )
            )
                errors.push({ fieldName: `${item.orderItem.uuid}_duplicated`, errorMessage: `Duplicated product.` });
        });

    orderItems
        .filter(item => isAddOnServiceOrderItem(item) && item.addOnService?.id)
        .forEach(item => {
            if (
                orderItems.some(
                    otherItem =>
                        isAddOnServiceOrderItem(item) &&
                        isAddOnServiceOrderItem(otherItem) &&
                        otherItem.orderItem.uuid !== item.orderItem.uuid &&
                        otherItem.addOnService?.id === item.addOnService?.id
                )
            )
                errors.push({
                    fieldName: `${item.orderItem.uuid}_duplicated`,
                    errorMessage: `Duplicated add-on service.`
                });
        });

    orderItems
        .filter(item => isCustomOrderItem(item) && isEmpty(item.orderItem.description))
        .forEach(item => {
            errors.push({ fieldName: `${item.orderItem.uuid}_description`, errorMessage: `Add a custom description.` });
        });

    orderItems
        .filter(item => isCustomOrderItem(item))
        .forEach(item => {
            if (
                orderItems.some(
                    otherItem =>
                        isCustomOrderItem(item) &&
                        isCustomOrderItem(otherItem) &&
                        otherItem.orderItem.uuid !== item.orderItem.uuid &&
                        otherItem.orderItem.description === item.orderItem.description
                )
            )
                errors.push({
                    fieldName: `${item.orderItem.uuid}_duplicated`,
                    errorMessage: `Duplicated custom description.`
                });
        });

    orderItems
        .filter(item => isCustomOrderItem(item) && isEmpty(item.orderItem.amount))
        .forEach(item => {
            errors.push({ fieldName: `${item.orderItem.uuid}_price`, errorMessage: `Add a price.` });
        });

    return errors;
};

const createEmptyOrder = (client: Client, timeZone: string, issuedAt?: moment.Moment): Order => {
    const currentDate = moment().tz(timeZone);

    return {
        id: 0,
        uuid: '',
        version: '',
        client: client,
        shipToClient: client,
        notes: '',
        items: {
            appointments: [],
            products: [],
            addOnServices: [],
            customs: [],
            memberships: []
        },
        issuedAt: issuedAt?.clone() ?? currentDate.clone(),
        createdAt: currentDate.clone(),
        updatedAt: currentDate.clone(),
        subtotal: '0.00',
        total: '0.00',
        paid: false
    };
};

enum ViewOptions {
    SALE = 'sale',
    NEW_CLIENT = 'new_client',
    PAYMENTS = 'payments',
    SUCCESS = 'success'
}

interface SingleSaleProps {
    editOrderId?: number;
    editSaleNumber?: number;
    onEditSaved?: () => void;
    onCancelEdit?: () => void;
    onMakeAnotherSale?: () => void;
}

type SaveAction = 'pay' | 'save';

const getOrderizableId = (item: GenericOrderItem): number | undefined => {
    if (isProductOrderItem(item)) {
        return item.product.businessProduct?.id ?? item.orderItem.orderizable.id;
    }

    if (isAddOnServiceOrderItem(item)) {
        return item.addOnService.id ?? item.orderItem.orderizable.id;
    }

    return item.orderItem.orderizable.id;
};

const getOrderItems = (order: Order): GenericOrderItem[] => [
    ...order.items.appointments,
    ...order.items.products,
    ...order.items.addOnServices,
    ...order.items.customs
];

const mergeOrderItemsAfterDelete = <T extends GenericOrderItem>(backendItems: T[], draftItems: T[]): T[] => {
    const draftItemsById = new Map(
        draftItems.filter(item => item.orderItem.id !== undefined).map(item => [item.orderItem.id, item])
    );
    const unsavedDraftItems = draftItems.filter(item => item.orderItem.id === undefined);

    return [
        ...backendItems.map(item => {
            if (item.orderItem.id === undefined) {
                return item;
            }

            return draftItemsById.get(item.orderItem.id) ?? item;
        }),
        ...unsavedDraftItems
    ];
};

const mergeOrderAfterDelete = (backendOrder: Order, draftOrder?: Order): Order => {
    if (!draftOrder) {
        return backendOrder;
    }

    return {
        ...backendOrder,
        client: draftOrder.client,
        shipToClient: draftOrder.shipToClient,
        notes: draftOrder.notes,
        issuedAt: draftOrder.issuedAt,
        items: {
            ...backendOrder.items,
            appointments: mergeOrderItemsAfterDelete(backendOrder.items.appointments, draftOrder.items.appointments),
            products: mergeOrderItemsAfterDelete(backendOrder.items.products, draftOrder.items.products),
            addOnServices: mergeOrderItemsAfterDelete(backendOrder.items.addOnServices, draftOrder.items.addOnServices),
            customs: mergeOrderItemsAfterDelete(backendOrder.items.customs, draftOrder.items.customs)
        }
    };
};

export const SingleSale: React.FC<SingleSaleProps> = props => {
    const dispatch = useDispatch();
    const timeZone = useTimeZone();
    const apiClientWrapper = useApiClientWrapper();

    const classes = useSingleSaleStyles();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<Array<FieldError>>([]);
    const [order, setOrder] = useState<Order | undefined>(undefined);
    const [selectedView, setSelectedView] = useState<ViewOptions>(ViewOptions.SALE);
    const [selectedClient, setSelectedClient] = useState<Client | undefined>(undefined);
    const [invoiceDate, setInvoiceDate] = useState<moment.Moment>(() => moment().tz(timeZone));
    const [saveAction, setSaveAction] = useState<SaveAction | null>(null);
    const [deletingOrderItemId, setDeletingOrderItemId] = useState<number | undefined>();
    const isEditMode = props.editOrderId !== undefined;

    const [checkout, setCheckout] = useState<{
        orderId?: number;
        show: boolean;
        collapse: boolean;
        editClientId: number | null;
    }>({ show: false, collapse: false, editClientId: null });

    const orderState = useSelector<RootState, OrdersState>(state => state.orders);
    const clientState = useSelector<RootState, ClientsState>(state => state.clients);

    const savedOrder = useSelector<RootState, Order | undefined>(state => state.orders.savedOrder);
    const loadedOrder = useSelector<RootState, Order | undefined>(state => state.orders.order);

    const [paidAmount, setPaidAmount] = useState(0);
    const isDeletingOrderItem = deletingOrderItemId !== undefined;

    const fromClientIdRef = React.useRef<number | null>(null);
    const [hideTips, setHideTips] = useState<boolean>(false);

    const returnToClientProfile = React.useCallback(() => {
        if (fromClientIdRef.current) {
            window.location.href = `/clients/${hashForUrl({ clientId: fromClientIdRef.current })}`;
            return true;
        }
        return false;
    }, []);

    const changeClientHandler = (clientId?: number) => {
        if (clientId) {
            setIsLoading(true);
            // Get client info, to create order.
            // Check useEffect for clientState.status
            dispatch(getClientThunk(apiClientWrapper, clientId));
        }

        if (!clientId) {
            setOrder(undefined);
            setSelectedClient(undefined);
        }
    };

    const clientSavedHandler = (client: Client) => {
        setSelectedView(ViewOptions.SALE);

        changeClientHandler(client.id);
    };

    const resetOrderHandler = () => {
        if (isEditMode) {
            props.onCancelEdit?.();
            return;
        }

        if (returnToClientProfile()) return;
        setOrder(undefined);
        setSelectedClient(undefined);
        setInvoiceDate(moment().tz(timeZone));
        setSelectedView(ViewOptions.SALE);
    };

    const makeAnotherSaleHandler = () => {
        resetOrderHandler();
        props.onMakeAnotherSale?.();
    };

    const changeInvoiceDateHandler = (date: moment.Moment) => {
        const nextDate = date.clone();

        setInvoiceDate(nextDate);
        setOrder(prev => (prev ? { ...prev, issuedAt: nextDate.clone() } : prev));
    };

    const removeOrderItemHandler = ({
        orderItem: removedItem,
        nextItems
    }: {
        orderItem: GenericOrderItem;
        nextItems: Order['items'];
    }) => {
        if (!order) {
            return;
        }

        if (isEditMode && order.id && removedItem.orderItem.id) {
            setDeletingOrderItemId(removedItem.orderItem.id);
            dispatch(deleteOrderItemThunk(apiClientWrapper, order.id, removedItem.orderItem.id));
            return;
        }

        setOrder({ ...order, items: nextItems });
    };

    const saveOrderHandler = (action: SaveAction) => {
        if (!order || isDeletingOrderItem) {
            return;
        }

        const errors = validateOrder(order);
        setErrors(errors);

        uniq(errors.map(error => error.errorMessage)).forEach(error => dispatch(showErrorThunk(error)));

        if (errors.length > 0) {
            return;
        }

        const saveOrder: SaveOrder = {
            id: isEditMode ? order.id : undefined,
            notes: (order.notes ?? '').slice(0, 500),
            uuid: order.uuid || uuid(),
            clientId: order.client.id!,
            shipToClientId: order.shipToClient.id!,
            issuedAt: order.issuedAt?.clone() ?? invoiceDate.clone(),
            items: getOrderItems(order).map(item => ({
                id: item.orderItem.id,
                uuid: item.orderItem.uuid || uuid(),
                orderId: item.orderItem.orderId ?? (isEditMode ? order.id : undefined),
                orderizable: {
                    type: item.orderItem.orderizable.type,
                    id: getOrderizableId(item)
                },
                petId: item.pet?.id,
                staffId: item.orderItem.staffId,
                taxId: item.orderItem.taxId,
                description: item.orderItem.description,
                quantity: item.orderItem.quantity,
                amount: item.orderItem.amount,
                discount: item.orderItem.discount,
                total: item.orderItem.total
            }))
        };

        setSaveAction(action);
        setIsLoading(true);

        dispatch(saveOrderThunk(apiClientWrapper, saveOrder));
    };

    const payOrderHandler = () => {
        saveOrderHandler('pay');
    };

    const saveHandler = () => {
        saveOrderHandler('save');
    };

    useEffect(() => {
        dispatch(fetchProductsThunk(apiClientWrapper, '', [], [apiClientWrapper.marketplaceId], [], 1, 10000));
        dispatch(fetchAddOnServicesThunk(apiClientWrapper));
        dispatch(fetchStaffThunk());

        if (props.editOrderId) {
            setIsLoading(true);
            dispatch(getOrderThunk(apiClientWrapper, props.editOrderId));
            return;
        }

        const id = sessionStorage.getItem('singleSaleFromClientId');
        if (id) {
            fromClientIdRef.current = parseInt(id, 10);
            sessionStorage.removeItem('singleSaleFromClientId');
            dispatch(getClientThunk(apiClientWrapper, fromClientIdRef.current));
        }
    }, []);

    useEffect(() => {
        if (
            !isEditMode ||
            !loadedOrder ||
            orderState.status !== OrdersStatus.GetSuccess ||
            loadedOrder.id !== props.editOrderId
        ) {
            return;
        }

        setIsLoading(false);
        setSelectedClient(loadedOrder.client);
        setInvoiceDate(loadedOrder.issuedAt?.clone() ?? moment().tz(timeZone));
        setOrder(loadedOrder);
        setDeletingOrderItemId(undefined);

        if (loadedOrder.client?.id != null) {
            dispatch(getClientProfileOverviewThunk(apiClientWrapper, loadedOrder.client.id));
        }
    }, [isEditMode, loadedOrder, orderState.status, props.editOrderId, timeZone]);

    useEffect(() => {
        if (isLoading && checkout.editClientId === null && clientState.status === ClientsStatus.GetSuccess) {
            const clientInfo = clientState.client as Client;

            setIsLoading(false);
            setSelectedClient(clientInfo);
            setOrder(createEmptyOrder(clientInfo, timeZone, invoiceDate));
            if (clientInfo.id != null) {
                dispatch(getClientProfileOverviewThunk(apiClientWrapper, clientInfo.id));
            }
        }
    }, [clientState.status]);

    useEffect(() => {
        if (fromClientIdRef.current && !selectedClient && clientState.status === ClientsStatus.GetSuccess) {
            const clientInfo = clientState.client as Client;
            setSelectedClient(clientInfo);
            setOrder(createEmptyOrder(clientInfo, timeZone, invoiceDate));
            if (clientInfo.id != null) {
                dispatch(getClientProfileOverviewThunk(apiClientWrapper, clientInfo.id));
            }
        }
    }, [clientState.status, selectedClient]);

    useEffect(() => {
        if (isLoading && orderState.status === OrdersStatus.SaveOrderSuccess) {
            const savedOrderData = savedOrder ?? order;
            const allItems = [
                ...(savedOrderData?.items?.appointments || []),
                ...(savedOrderData?.items?.products || []),
                ...(savedOrderData?.items?.addOnServices || []),
                ...(savedOrderData?.items?.customs || [])
            ];

            const noStaffInOrder = allItems.every(item => !item.orderItem.staffId);

            setHideTips(noStaffInOrder);
            setIsLoading(false);

            if (saveAction === 'save') {
                setSaveAction(null);

                if (isEditMode) {
                    props.onEditSaved?.();
                    return;
                }

                resetOrderHandler();
                return;
            }

            setSaveAction(null);
            setCheckout({
                orderId: savedOrder?.id ?? order?.id,
                show: true,
                collapse: false,
                editClientId: null
            });
        }
    }, [orderState.status]);

    useEffect(() => {
        if (
            !isEditMode ||
            !loadedOrder ||
            orderState.status !== OrdersStatus.DeleteOrderItemSuccess ||
            loadedOrder.id !== props.editOrderId
        ) {
            return;
        }

        setOrder(prev => mergeOrderAfterDelete(loadedOrder, prev));
        setDeletingOrderItemId(undefined);
    }, [isEditMode, loadedOrder, orderState.status, props.editOrderId]);

    useEffect(() => {
        if (
            orderState.status !== OrdersStatus.Error &&
            orderState.status !== OrdersStatus.NotFound &&
            orderState.status !== OrdersStatus.Unprocessable
        ) {
            return;
        }

        setDeletingOrderItemId(undefined);
        setIsLoading(false);
        setSaveAction(null);
    }, [orderState.status]);

    if (selectedView === ViewOptions.NEW_CLIENT) {
        return (
            <OverFullWindow onClose={() => setSelectedView(ViewOptions.SALE)}>
                <ClientPage
                    hideHeader={true}
                    onClientSaved={clientSavedHandler}
                    onClose={() => setSelectedView(ViewOptions.SALE)}
                    onPetSaved={() => setSelectedView(ViewOptions.SALE)}
                />
            </OverFullWindow>
        );
    }

    if (selectedView === ViewOptions.SUCCESS) {
        return (
            <SingleSaleSuccess
                order={savedOrder!}
                paidAmount={paidAmount ?? 0}
                onBack={resetOrderHandler}
                onMakeAnotherSale={makeAnotherSaleHandler}
                fromClientId={fromClientIdRef.current}
            />
        );
    }

    const checkoutPaidHandler = (paidAmount: number) => {
        setPaidAmount(paidAmount);
        setCheckout(prev => ({ ...prev, orderId: undefined, show: false, collapse: false, editClientId: null }));
        setSelectedView(ViewOptions.SUCCESS);
    };

    const checkoutPayByLinkSentHandler = () => {
        setCheckout(prev => ({ ...prev, orderId: undefined, show: false, collapse: false, editClientId: null }));
        resetOrderHandler();
    };

    const checkoutEditClientHandler = (clientId: number) => {
        setCheckout(prev => ({ ...prev, collapse: true, editClientId: clientId }));
    };

    const closeCheckoutEditClientHandler = () => {
        setCheckout(prev => ({ ...prev, collapse: false, editClientId: null }));
    };

    return (
        <>
            <div className={classes.root}>
                {isEditMode && isLoading && !order ? (
                    <div className={classes.loadingContainer}>
                        <Spinner />
                    </div>
                ) : (
                    <>
                        <SingleSaleHeader
                            errors={errors}
                            data={selectedClient!}
                            date={invoiceDate}
                            timeZone={timeZone}
                            title={
                                isEditMode
                                    ? `Edit Sale${props.editSaleNumber ? ` #${props.editSaleNumber}` : ''}`
                                    : undefined
                            }
                            onBack={
                                isEditMode
                                    ? props.onCancelEdit
                                    : fromClientIdRef.current
                                    ? returnToClientProfile
                                    : undefined
                            }
                            onAddNewClient={() => setSelectedView(ViewOptions.NEW_CLIENT)}
                            onClientSelected={changeClientHandler}
                            onDateChange={changeInvoiceDateHandler}
                        />

                        {order && (
                            <SingleSaleTable
                                order={order}
                                errors={errors}
                                isLoading={isLoading || isDeletingOrderItem}
                                deletingOrderItemId={deletingOrderItemId}
                                onChange={setOrder}
                                onRemoveOrderItem={removeOrderItemHandler}
                                onPay={payOrderHandler}
                                onSave={saveHandler}
                            />
                        )}
                    </>
                )}
            </div>
            {checkout.show && checkout.orderId && (
                <Checkout
                    orderId={checkout.orderId}
                    onClose={() => {
                        if (!returnToClientProfile()) {
                            setCheckout(prev => ({ ...prev, orderId: undefined, show: false, collapse: false }));
                        }
                    }}
                    onPaid={checkoutPaidHandler}
                    onPayByLinkSent={checkoutPayByLinkSentHandler}
                    hide={checkout.collapse}
                    hideTips={hideTips}
                    onEditClient={checkoutEditClientHandler}
                />
            )}
            {checkout.editClientId !== null && (
                <OverFullWindow onClose={closeCheckoutEditClientHandler}>
                    <ClientPage
                        clientId={checkout.editClientId}
                        hideHeader={true}
                        onCloseModal={closeCheckoutEditClientHandler}
                        onClientSaved={closeCheckoutEditClientHandler}
                    />
                </OverFullWindow>
            )}
        </>
    );
};
